import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ENGLISH_FALLBACK } from './english-fallback.generated';

export interface LanguageMetadata {
  ranking: number;
  code: string;
  locale: string;
  name: string;
  nativeName: string;
  direction: 'ltr' | 'rtl';
}

interface LocaleCatalog extends LanguageMetadata {
  ui: Record<string, string>;
  domains: Record<string, string>;
  bands: Record<string, string>;
}

const englishCatalog: LocaleCatalog = {
  ...ENGLISH_FALLBACK,
  direction: 'ltr',
  ui: { ...ENGLISH_FALLBACK.ui },
  domains: { ...ENGLISH_FALLBACK.domains },
  bands: { ...ENGLISH_FALLBACK.bands },
};
const englishMetadata: LanguageMetadata = {
  ranking: englishCatalog.ranking,
  code: englishCatalog.code,
  locale: englishCatalog.locale,
  name: englishCatalog.name,
  nativeName: englishCatalog.nativeName,
  direction: englishCatalog.direction,
};

@Injectable({ providedIn: 'root' })
export class LocalizationService {
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly current = signal<LocaleCatalog>(englishCatalog);
  readonly languages = signal<LanguageMetadata[]>([englishMetadata]);
  readonly language = computed(() => this.current().code);
  readonly locale = computed(() => this.current().locale);
  readonly direction = computed(() => this.current().direction);
  private readonly loadedCatalogs = new Map<string, LocaleCatalog>([['en', englishCatalog]]);
  private readonly pendingCatalogs = new Map<string, Promise<LocaleCatalog>>();

  async initialize(): Promise<void> {
    if (!this.isBrowser) {
      this.useEnglishFallback();
      return;
    }

    try {
      const registry = await firstValueFrom(
        this.http.get<{ defaultLanguage: string; languages: LanguageMetadata[] }>(
          '/api/v1/localization',
        ),
      );
      this.languages.set(
        [...registry.languages].sort((left, right) => left.ranking - right.ranking),
      );
      const routeLanguage = this.languageFromPath();
      const stored = this.storage()?.getItem('iqlabtest-language');
      const preferred =
        routeLanguage ??
        (stored ? this.normalize(stored) : this.detectBrowserLanguage(registry.defaultLanguage));
      await this.setLanguage(preferred);
    } catch {
      this.useEnglishFallback();
    }
  }

  async setLanguage(language: string): Promise<void> {
    const code = this.normalize(language);
    const cached = this.loadedCatalogs.get(code);
    if (cached) {
      this.applyCatalog(cached);
      return;
    }

    let request = this.pendingCatalogs.get(code);
    if (!request) {
      request = firstValueFrom(
        this.http.get<LocaleCatalog>(`/api/v1/localization/${encodeURIComponent(code)}`),
      ).catch(() => englishCatalog);
      this.pendingCatalogs.set(code, request);
    }

    const catalog = await request;
    this.pendingCatalogs.delete(code);
    this.loadedCatalogs.set(catalog.code, catalog);
    this.applyCatalog(catalog);
  }

  private languageFromPath(): string | null {
    const path = this.isBrowser ? location.pathname : (this.document.location?.pathname ?? '/');
    const candidate = /^\/([a-z]{2,3})(?:\/|$)/u.exec(path)?.[1];
    return candidate ? this.matchLanguage(candidate) : null;
  }

  private applyCatalog(catalog: LocaleCatalog): void {
    this.current.set(catalog);
    this.storage()?.setItem('iqlabtest-language', catalog.code);
    this.document.documentElement.lang = catalog.code;
    this.document.documentElement.dir = catalog.direction;
    const path = this.isBrowser ? location.pathname : (this.document.location?.pathname ?? '/');
    const titleKey = path.startsWith('/iq-test')
      ? 'iq.assessment'
      : path.startsWith('/verify')
        ? 'verify.verified'
        : path.startsWith('/company')
          ? 'company.header'
          : path.startsWith('/candidate')
            ? 'candidate.header'
            : path.startsWith('/consumer')
              ? 'consumer.header'
              : path.startsWith('/admin')
                ? 'admin.header'
                : 'about.status';
    this.document.title = `IQLabTest — ${catalog.ui[titleKey] ?? 'Cognitive assessment'}`;
  }

  t(key: string): string {
    return this.current().ui[key] ?? englishCatalog.ui[key] ?? key;
  }

  domain(key: string): string {
    return this.current().domains[key] ?? englishCatalog.domains[key] ?? key;
  }

  band(key: string): string {
    return this.current().bands[key] ?? englishCatalog.bands[key] ?? key;
  }

  private useEnglishFallback(): void {
    this.languages.set([englishMetadata]);
    this.applyCatalog(englishCatalog);
  }

  private normalize(value: string): string {
    return this.matchLanguage(value) ?? 'en';
  }

  private detectBrowserLanguage(defaultLanguage: string): string {
    if (!this.isBrowser) return this.matchLanguage(defaultLanguage) ?? 'en';

    const candidates = [
      ...(navigator.languages ?? []),
      navigator.language,
      Intl.DateTimeFormat().resolvedOptions().locale,
    ].filter((value): value is string => Boolean(value));

    for (const candidate of candidates) {
      const match = this.matchLanguage(candidate);
      if (match) return match;
    }

    return this.matchLanguage(defaultLanguage) ?? 'en';
  }

  private matchLanguage(value: string): string | null {
    const lowered = value.trim().toLowerCase().replace('_', '-');
    if (!lowered) return null;
    return (
      this.languages().find(
        (item) => item.code.toLowerCase() === lowered || item.locale.toLowerCase() === lowered,
      )?.code ??
      this.languages().find((item) => lowered.startsWith(`${item.code.toLowerCase()}-`))?.code ??
      null
    );
  }

  private storage(): Storage | null {
    if (!this.isBrowser) return null;
    try {
      return globalThis.localStorage ?? null;
    } catch {
      return null;
    }
  }
}
